<template>
  <loading-element v-if="authStore.isLoading" />
  <div class="main bg-primary" v-else>
    <div class="rowSpaceBetween fullWidth mb-5">
      <TopDeck
        v-if="authStore.userInfos"
        firstElement="Mes"
        secondElement="recettes"
        :coloredSecond="true"
      />
      <div class="rowCustom">
        <ThemeToggle />
        <AccountToggle />
      </div>
    </div>

    <!-- Bannière catalogue -->
    <div class="mr-banner rounded-xl mb-6" @click="router.push('/browse_base_recipes')">
      <div>
        <h2 class="mr-banner__title">Envie de nouveautés ?</h2>
        <p class="mr-banner__sub">Parcourez notre sélection et ajoutez des recettes à votre collection</p>
      </div>
      <button class="mr-banner__btn">
        <v-icon size="15">mdi-book-open-page-variant</v-icon>
        Parcourir
      </button>
    </div>

    <div class="mr-container">
      <!-- Header -->
      <div class="mr-header">
        <div class="mr-header__left">
          <h1 class="mr-header__title">Mes recettes</h1>
          <span class="mr-header__count">{{ filteredRecipes.length }} recette{{ filteredRecipes.length !== 1 ? 's' : '' }}</span>
        </div>
        <div class="mr-header__right">
          <div class="mr-search">
            <v-icon size="15" class="mr-search__icon">mdi-magnify</v-icon>
            <input
              v-model="searchQuery"
              class="mr-search__input"
              placeholder="Rechercher…"
            />
          </div>
          <button class="mr-btn-create" @click="router.push('/add_recipe')">
            <v-icon size="15">mdi-plus</v-icon>
            Créer une recette
          </button>
        </div>
      </div>

      <!-- Pills filtres catégories -->
      <div class="mr-filters">
        <button
          v-for="cat in categories"
          :key="cat"
          class="mr-pill"
          :class="{ 'mr-pill--active': selectedCategory === cat }"
          @click="selectedCategory = cat"
        >
          {{ cat === 'Toutes' ? 'Toutes' : $t(cat) }}
        </button>
      </div>

      <!-- Loader -->
      <div v-if="isLoadingRecipes" class="mr-loader">
        <v-progress-circular indeterminate color="accent" size="28" />
      </div>

      <!-- Vide -->
      <div v-else-if="!filteredRecipes.length" class="mr-empty">
        <v-icon size="36" style="opacity:0.25">mdi-food-off</v-icon>
        <p>Aucune recette à afficher.</p>
      </div>

      <!-- Grille -->
      <div v-else class="mr-grid">
        <RecipeCard
          v-for="item in filteredRecipes"
          :key="item.id"
          :item="item"
          @toggle-favorite="toggleFavoriteOn"
          @delete="deleteRecipe"
        />
      </div>
    </div>

    <v-snackbar v-if="errorMessage" color="error" :timeout="3000">
      {{ errorMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import LoadingElement from "@/components/Animations/LoadingElement.vue";
import TopDeck from "@/components/Utils/TopDeck.vue";
import { useAuthStore } from "@/store/auth_store";
import {
  getUserRecipes,
  deleteRecipeFromUserCollection,
  setRecipeAsFavorite,
} from "@/service/recipe_service";
import ThemeToggle from "@/components/Utils/ThemeToggle.vue";
import AccountToggle from "@/components/Utils/AccountToggle.vue";
import RecipeCard from "./RecipeCard.vue";

export default {
  name: "MyRecipes",
  components: { LoadingElement, TopDeck, ThemeToggle, AccountToggle, RecipeCard },
  setup() {
    const authStore = useAuthStore();
    const errorMessage = ref("");
    const router = useRouter();
    const recipes = ref([]);
    const isLoadingRecipes = ref(false);
    const searchQuery = ref("");
    const selectedCategory = ref("Toutes");

    const CATEGORY_ORDER = ["Breakfast", "Starter", "Meal", "Dessert", "Snacks"];

    const categories = computed(() => {
      const cats = new Set(recipes.value.map((r) => r.category).filter(Boolean));
      const sorted = CATEGORY_ORDER.filter((c) => cats.has(c));
      [...cats].forEach((c) => { if (!CATEGORY_ORDER.includes(c)) sorted.push(c); });
      return ["Toutes", ...sorted];
    });

    const filteredRecipes = computed(() => {
      const q = searchQuery.value.trim().toLowerCase();
      const cat = selectedCategory.value;
      return recipes.value.filter((item) => {
        const title = (item.title || "").toLowerCase();
        const category = String(item.category || "").toLowerCase();
        const notes = (item.notes || "").toLowerCase();
        const matchSearch = !q || title.includes(q) || category.includes(q) || notes.includes(q);
        const matchCategory = cat === "Toutes" || item.category === cat;
        return matchSearch && matchCategory;
      });
    });

    async function deleteRecipe(id) {
      if (!id || !authStore.userid) return;
      try {
        await deleteRecipeFromUserCollection(authStore.userid, id);
        recipes.value = recipes.value.filter((r) => r.id !== id);
      } catch (err) {
        console.error("❌ Erreur suppression :", err);
        errorMessage.value = "Erreur lors de la suppression de la recette.";
      }
    }

    async function toggleFavoriteOn(item) {
      if (!item?.id || !authStore.userid) return;
      try {
        const newValue = !item.favorite;
        await setRecipeAsFavorite(authStore.userid, item.id, newValue);
        item.favorite = newValue;
      } catch (err) {
        console.error("❌ Erreur favoris :", err);
        errorMessage.value = "Erreur lors de la mise à jour du favori.";
      }
    }

    onMounted(() => {
      watch(
        () => authStore.userInfos,
        async () => {
          if (!authStore?.userid) return;
          try {
            isLoadingRecipes.value = true;
            const data = await getUserRecipes(authStore.userid);
            recipes.value = Object.entries(data).map(([id, value]) => ({ id, ...value }));
          } catch (error) {
            console.error("Erreur récupération recettes:", error);
            errorMessage.value = "Erreur lors du chargement des recettes.";
          } finally {
            isLoadingRecipes.value = false;
          }
        },
        { immediate: true }
      );
    });

    return {
      authStore,
      errorMessage,
      router,
      recipes,
      searchQuery,
      selectedCategory,
      categories,
      filteredRecipes,
      isLoadingRecipes,
      deleteRecipe,
      toggleFavoriteOn,
    };
  },
};
</script>

<style scoped>
/* Bannière */
.mr-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(135deg, rgb(var(--v-theme-accent)), rgba(255,255,255,0.55));
  color: white;
  cursor: pointer;
  gap: 12px;
}
.mr-banner__title {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 4px;
}
.mr-banner__sub {
  font-size: 12px;
  margin: 0;
  opacity: 0.85;
}
.mr-banner__btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #ffffff !important;
  border: none;
  border-radius: 999px;
  color: rgb(var(--v-theme-accent)) !important;
  font-size: 12px;
  font-weight: 600;
  padding: 7px 16px;
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;
  transition: opacity 0.15s;
  flex-shrink: 0;
}
.mr-banner__btn:hover {
  opacity: 0.88;
}

/* Container principal */
.mr-container {
  background: rgb(var(--v-theme-secondary));
  border-radius: 16px;
  padding: 20px 20px 28px;
}

/* Header */
.mr-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 18px;
}
.mr-header__left {
  display: flex;
  align-items: baseline;
  gap: 10px;
}
.mr-header__title {
  font-size: 17px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
  margin: 0;
}
.mr-header__count {
  font-size: 12px;
  color: rgba(var(--v-theme-text), 0.4);
}
.mr-header__right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

/* Recherche */
.mr-search {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgb(var(--v-theme-tertiary));
  border: 0.5px solid rgba(var(--v-theme-text), 0.1);
  border-radius: 999px;
  padding: 7px 14px;
}
.mr-search__icon {
  color: rgba(var(--v-theme-text), 0.35);
  flex-shrink: 0;
}
.mr-search__input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 13px;
  color: rgb(var(--v-theme-text));
  font-family: inherit;
  width: 160px;
}
.mr-search__input::placeholder {
  color: rgba(var(--v-theme-text), 0.35);
}

/* Bouton créer */
.mr-btn-create {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #1A1615;
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.15s;
}
.mr-btn-create:hover {
  opacity: 0.85;
}

/* Pills filtres */
.mr-filters {
  display: flex;
  gap: 7px;
  margin-bottom: 20px;
  overflow-x: auto;
  padding-bottom: 2px;
  scrollbar-width: none;
}
.mr-filters::-webkit-scrollbar { display: none; }

.mr-pill {
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  white-space: nowrap;
  border: 0.5px solid rgba(var(--v-theme-text), 0.18);
  color: rgba(var(--v-theme-text), 0.6);
  background: transparent;
  font-family: inherit;
  transition: all 0.15s;
}
.mr-pill:hover:not(.mr-pill--active) {
  background: rgba(var(--v-theme-text), 0.05);
}
.mr-pill--active {
  background: #1A1615;
  color: #fff;
  border-color: #1A1615;
}

/* Loader / vide */
.mr-loader, .mr-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 0;
  color: rgba(var(--v-theme-text), 0.35);
  font-size: 13px;
}

/* Grille */
.mr-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 20px;
}

/* Utils */
.rowCustom {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
}
</style>
